import { useCallback, useRef } from 'react'

/**
 * Fermeture d'une pop-in au clic sur son scrim — mais SEULEMENT si le geste a
 * commencé ET s'est terminé sur le scrim.
 *
 * Le navigateur dispatche `click` sur l'ANCÊTRE COMMUN du mousedown et du
 * mouseup. Sélectionner du texte dans le panneau puis relâcher la souris
 * au-dessus du scrim fait donc naître un `click` porté par le scrim lui-même :
 * le `onClick={e => e.stopPropagation()}` du panneau n'est jamais traversé, et
 * la pop-in se fermait en pleine sélection (idem pour un slider ou un
 * glisser-déposer qui déborde du cadre).
 *
 * Les deux écouteurs sont en phase de CAPTURE : ils doivent voir le geste même
 * quand le panneau stoppe la propagation, sinon un état périmé (un appui sur le
 * scrim jamais relâché dans la fenêtre) ferait fermer au geste suivant.
 *
 * @param {() => void} onClose — appelé quand le scrim est réellement cliqué.
 * @param {boolean} [enabled=true] — faux → le scrim ne ferme plus (ex. save en
 *   cours). Le suivi des cibles continue, l'état reste donc cohérent.
 * @returns {{onMouseDownCapture: Function, onMouseUpCapture: Function, onClick: Function}}
 *   Props à étaler sur l'élément scrim.
 */
export const useScrimDismiss = (onClose, enabled = true) => {
  const pressedScrim = useRef(false)
  const releasedScrim = useRef(false)

  const onMouseDownCapture = useCallback((e) => {
    pressedScrim.current = e.target === e.currentTarget
    releasedScrim.current = false
  }, [])

  const onMouseUpCapture = useCallback((e) => {
    releasedScrim.current = e.target === e.currentTarget
  }, [])

  const onClick = useCallback((e) => {
    // Une pop-in peut s'ouvrir par-dessus une autre : son scrim retient le clic
    // pour que le scrim du dessous ne ferme pas les deux d'un coup.
    e.stopPropagation()
    const onScrim = pressedScrim.current && releasedScrim.current && e.target === e.currentTarget
    pressedScrim.current = false
    releasedScrim.current = false
    if (onScrim && enabled) onClose()
  }, [enabled, onClose])

  return { onMouseDownCapture, onMouseUpCapture, onClick }
}
